import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, take, tap, throwError, map, filter } from 'rxjs';
import { SortDirection } from '@angular/material/sort';
import { environment } from '@env/environment';

export interface Student {
  t_ot_studentid?: number;
  name?: string;
  email?: string;
  info?: string;
  referal?: string;
  department?: string;
  picture?: string;
  post?: string;
  info1?: string;
  info2?: string;
  info4?: string;
  phone2?: string;
  reminder?: number;
  last_course_start?: Date;
  last_course_title?: string;
  last_course_status2?: string;
  lang?: string;
  info3?: string;
  last_login?: string;
  last_activity?: string;
  info5?: string;
  mobile?: string;
  created?: Date;
  createdby?: number;
  modified?: Date;
  modifiedby?: number;
  company_id?: number;
  skype_id?: string;
  advisor_id?: number;
  supervisor_id?: number;
  registration_date?: Date;
  street?: string;
  phone?: string;
  company?: string;
  zip?: string;
  city?: string;
  country?: string;
  account_id?: number;
  beusergroup_id?: number;
  div_id?: number;
  status_id?: number;
  mandatory_steps?: number;
  twin_id?: number;
  uuid?: string;
  profile_image?: string;
  // Enriched fields from API
  status_bucket?: string;
  is_resign?: boolean;
}

export interface StudentSingle {
  data: Student;
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface StudentTable {
  data: Student[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface StudentStudyActivityDay {
  day: number;
  date: string;
  study_minutes: number;
  points: number;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

@Injectable({ providedIn: 'root' })
export class StudentsService {
  // Private
  private _student: BehaviorSubject<Student | null> = new BehaviorSubject(null);
  private _students: BehaviorSubject<Student[] | null> = new BehaviorSubject(null);
  private _studentsTable: BehaviorSubject<StudentTable | null> = new BehaviorSubject(null);

  readonly bucketCounts$ = new BehaviorSubject<Record<string, number>>({});
  readonly accountStatusCounts$ = new BehaviorSubject<Record<string, number>>({});

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for student
   */
  get student$(): Observable<Student> {
    return this._student.asObservable();
  }

  /**
   * Getter for students
   */
  get students$(): Observable<Student[]> {
    return this._students.asObservable();
  }

  /**
   * Getter for students table
   */
  get studentsTable$(): Observable<StudentTable> {
    return this._studentsTable.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get students with pagination and filtering
   * @param companyId        - Optional company ID filter (for global admins to filter by company)
   * @param accountStatus    - Account status filter: active|inactive|new|deleted (default: active)
   * @param progressFilter   - Journey-pipeline bucket key to filter by (optional)
   */
  getStudents(
    pageNumber: number = 1,
    pageSize: number = 20,
    sort: string = '',
    order: SortDirection = '',
    searchTerm: string = '',
    filtersArgs: string = '',
    companyId: number | null = null,
    accountStatus: string = 'active',
    progressFilter: string = ''
  ): Observable<StudentTable> {
    const apiUrl = `${environment.serverUrl}/v1/students`;

    let params = new HttpParams()
      .set('page', '' + pageNumber)
      .set('per_page', '' + pageSize)
      .set('sort', sort)
      .set('sorder', order)
      .set('search', searchTerm)
      .set('filters', filtersArgs)
      .set('account_status', accountStatus);

    if (companyId !== null) {
      params = params.set('company_id', '' + companyId);
    }
    if (progressFilter) {
      params = params.set('progress_filter', progressFilter);
    }

    return this._httpClient
      .get<any>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        }),
        params,
      })
      .pipe(
        tap((response: any) => {
          // Populate count BehaviorSubjects from the raw response
          if (response.bucket_counts) {
            this.bucketCounts$.next(response.bucket_counts);
          }
          if (response.account_status_counts) {
            this.accountStatusCounts$.next(response.account_status_counts);
          }
        }),
        map((response: any) => {
          const students = response.data || response.students || [];
          const table: StudentTable = {
            data: students,
            page: response.page || 1,
            per_page: response.per_page || pageSize,
            total: response.total || students.length,
            total_pages: response.total_pages || 0,
          };
          return table;
        }),
        tap((table: StudentTable) => {
          this._studentsTable.next(table);
          this._students.next(table.data);
        })
      );
  }

  /**
   * Search students with given query
   * @param companyId - Optional company ID filter
   */
  searchStudents(query: string, companyId: number | null = null): Observable<Student[]> {
    return this.getStudents(1, 20, '', '', query, '', companyId).pipe(
      map((response: StudentTable) => response.data)
    );
  }

  /**
   * Get student by id
   */
  getStudentById(id: number): Observable<Student> {
    const apiUrl = `${environment.serverUrl}/v1/students/${id}`;

    return this._httpClient
      .get<StudentSingle>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        }),
        params: new HttpParams().set('id', '' + id),
      })
      .pipe(
        map((response: StudentSingle) => response.data),
        tap((student: Student) => {
          this._student.next(student);
        })
      );
  }

  /**
   * Create student
   */
  createStudent(studentData: Partial<Student>): Observable<Student> {
    return this._httpClient
      .post<any>(`${environment.serverUrl}/v1/students`, studentData, httpOptions)
      .pipe(
        map((response) => response.data),
        switchMap((newStudent) =>
          this.students$.pipe(
            take(1),
            map((students) => {
              const updatedStudents = [newStudent, ...(students || [])];
              this._students.next(updatedStudents);
              const currentTable = this._studentsTable.value;
              if (currentTable) {
                this._studentsTable.next({
                  ...currentTable,
                  data: updatedStudents,
                  total: currentTable.total + 1,
                });
              }
              return newStudent;
            })
          )
        )
      );
  }

  /**
   * Bulk import students from a pre-parsed list.
   * Optionally enroll them all in a course.
   */
  importStudents(
    students: Array<Partial<Student>>,
    companyId: number | null = null,
    courseId: number | null = null
  ): Observable<any> {
    return this._httpClient
      .post<any>(
        `${environment.serverUrl}/v1/students/import`,
        { students, company_id: companyId, course_id: courseId },
        httpOptions
      )
      .pipe(
        tap((result) => {
          // Refresh the student list after a bulk import
          if (result?.created > 0) {
            const currentCompanyId = companyId;
            this.getStudents(1, 20, '', '', '', '', currentCompanyId).subscribe();
          }
        })
      );
  }

  /**
   * Update student
   */
  updateStudent(student: Student): Observable<Student> {
    const id = student.t_ot_studentid;
    const data = {
      uuid: '' + student.uuid,
    };

    return this._httpClient
      .post<any>(`${environment.serverUrl}/v1/students/${id}`, JSON.stringify(data), httpOptions)
      .pipe(
        map((response) => response.data),
        switchMap((updatedStudent) =>
          this.students$.pipe(
            take(1),
            map((students) => {
              if (students) {
                // Find the index of the updated student
                const index = students.findIndex((item) => item.t_ot_studentid === id);

                if (index !== -1) {
                  // Update the student
                  students[index] = updatedStudent;

                  // Update the students
                  this._students.next(students);

                  // Update the student table if it exists
                  const currentTable = this._studentsTable.value;
                  if (currentTable) {
                    const updatedTable = {
                      ...currentTable,
                      data: students,
                    };
                    this._studentsTable.next(updatedTable);
                  }
                }
              }

              // Update the selected student if it's the same one
              const currentStudent = this._student.value;
              if (currentStudent && currentStudent.t_ot_studentid === id) {
                this._student.next(updatedStudent);
              }

              return updatedStudent;
            })
          )
        )
      );
  }

  /**
   * Delete student
   */
  deleteStudent(id: number): Observable<boolean> {
    return this.students$.pipe(
      take(1),
      switchMap((students) =>
        this._httpClient.delete(`${environment.serverUrl}/v1/students/${id}`, httpOptions).pipe(
          map(() => {
            if (students) {
              // Find the index of the deleted student
              const index = students.findIndex((item) => item.t_ot_studentid === id);

              if (index !== -1) {
                // Delete the student
                students.splice(index, 1);

                // Update the students
                this._students.next(students);

                // Update the student table if it exists
                const currentTable = this._studentsTable.value;
                if (currentTable) {
                  const updatedTable = {
                    ...currentTable,
                    data: students,
                    total: currentTable.total - 1,
                  };
                  this._studentsTable.next(updatedTable);
                }
              }
            }

            // Clear the selected student if it's the same one
            const currentStudent = this._student.value;
            if (currentStudent && currentStudent.t_ot_studentid === id) {
              this._student.next(null);
            }

            return true;
          })
        )
      )
    );
  }

  /**
   * Get student by parameters
   */
  getStudentByParams(data: any): Observable<Student[]> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const httpParams = new HttpParams({ fromObject: data });
    const options = { params: httpParams, headers: headers };

    return this._httpClient.get<Student[]>(`${environment.serverUrl}/v1/students/`, options);
  }

  /**
   * Reset the selected student
   */
  resetStudent(): void {
    this._student.next(null);
  }

  /**
   * Reset the students list
   */
  resetStudents(): void {
    this._students.next(null);
    this._studentsTable.next(null);
  }

  /**
   * Set the selected student
   */
  setSelectedStudent(student: Student | null): void {
    this._student.next(student);
  }

  /**
   * Update student with partial data via PUT
   * @param studentId Student ID
   * @param data Partial student data to update
   */
  updateStudentData(studentId: number, data: Partial<Student>): Observable<Student> {
    return this._httpClient.put<any>(
      `${environment.serverUrl}/v1/students/${studentId}`,
      data,
      httpOptions
    ).pipe(
      map((response) => response.data),
      tap((updatedStudent) => {
        // Update the cached student if it's the same one
        const currentStudent = this._student.value;
        if (currentStudent && currentStudent.t_ot_studentid === studentId) {
          this._student.next({ ...currentStudent, ...updatedStudent });
        }
        
        // Update the students array if needed
        const currentStudents = this._students.value;
        if (currentStudents) {
          const index = currentStudents.findIndex(s => s.t_ot_studentid === studentId);
          if (index !== -1) {
            currentStudents[index] = { ...currentStudents[index], ...updatedStudent };
            this._students.next([...currentStudents]);
          }
        }
      })
    );
  }

  getStudentStudyActivity(studentId: number, year: number, month: number): Observable<StudentStudyActivityDay[]> {
    let params = new HttpParams().set('year', String(year)).set('month', String(month));
    return this._httpClient
      .get<any>(`${environment.serverUrl}/v1/students/${studentId}/study-activity`, { params })
      .pipe(map((res) => res?.data || []));
  }
} 